/**
 * Non-topology-mutating edit-mode actions: enter/exit edit mode,
 * selection (click, box, connected, vertex groups), hide/unhide,
 * vertex-group + material-slot management, granularity toggles,
 * and live vertex transforms.
 *
 * Topology-changing ops live in `meshOps.ts`; curve-specific ops
 * live in `curveOps.ts`; shared infrastructure lives in `core.ts`.
 */

import * as THREE from "three/webgpu";
import { buildCurveEditVertices } from "../../modeling/curveOps.ts";
import {
  applyBoxSelection,
  applyBrushSelection,
  type BrushSelectionMode,
  captureEditSelectionForRecall,
  convertSelectionForGranularity,
  deriveVerticesFromFaces,
  hasAnySelection,
  hasFaceSelection,
  hasVertexSelection,
  isFaceMode,
  propagateHiddenVerticesToFaces,
  selectConnectedEditSelection,
  selectSimilarFacesByNormal,
  syncHiddenVerticesToFaces,
  toggleSelection,
  toggleSelectionBatch,
} from "../../modeling/editSelection.ts";
import {
  applyMirrorClipping,
  collectClipPlanes,
} from "../../modeling/mirrorClipping.ts";
import {
  createSceneSurfaceProjectContext,
  disposeSceneSurfaceProjectContext,
  queryNearestSceneSurface,
} from "../../modeling/sceneSurface.ts";
import {
  getAdjacencyCache,
  topologyFaceCount,
  vertexNeighbors,
} from "../../modeling/topology.ts";
import {
  isCurveObject,
  isLightObject,
  isMannequinObject,
} from "../../modeling/types.ts";
import {
  composeSceneObjectWorldMatrix,
  getCollectionForObject,
} from "../collectionGraph.ts";
import { clearLiveEditPreview } from "../liveEditPreview.ts";
import { patchSceneObject } from "../sceneObjectMap.ts";
import { captureSelectionSeed } from "../storeCommandOps/replayStrategies.ts";
import type { InteractionMode, LastOperation } from "../storeTypes.ts";
import {
  captureSelectionDeltaState,
  pushSelectionUndo,
  undoManager,
} from "../storeUndo.ts";
import {
  computeNormalsForObject,
  type Get_,
  patchSceneObjectAfterEdit,
  type Set_,
} from "./core.ts";

// ---------------------------------------------------------------------------
// Vertex-selection-mode reducer — shared by vertex-group + future ops
// ---------------------------------------------------------------------------

/** Selection-mode reducer used by group-based selection actions (and future
 *  "Select Similar" / "Select Linked" features). Pure — never mutates inputs. */
export type VertexSelectionMode = "replace" | "add" | "subtract";
export function applyVertexSelectionMode(
  current: Set<number>,
  incoming: ReadonlySet<number>,
  mode: VertexSelectionMode,
): Set<number> {
  if (mode === "replace") return new Set(incoming);
  const next = new Set(current);
  if (mode === "add") {
    for (const v of incoming) next.add(v);
  } else {
    for (const v of incoming) next.delete(v);
  }
  return next;
}

const _surfaceWorldMatrix = new THREE.Matrix4();
const _surfaceInverseMatrix = new THREE.Matrix4();
const _surfaceWorldPoint = new THREE.Vector3();
const _surfaceLocalPoint = new THREE.Vector3();

// ---------------------------------------------------------------------------
// createSelectionOps factory — composes into createEditOps
// ---------------------------------------------------------------------------

export function createSelectionOps(set: Set_, get: Get_) {
  return {
    enterEditMode: (objectId: string) => {
      const obj = get().sceneObjects.get(objectId);
      if (!obj || isLightObject(obj) || isMannequinObject(obj)) return;
      const collection = getCollectionForObject(get().collections, objectId);
      if (obj.generator || collection?.generatorRef) return;
      // No undo snapshot for mode transitions: applySnapshot infers the target
      // mode from snap.editSelection, so the extra push would be a deep-clone
      // of the whole scene with no new data. Cycling edit <-> object mode used
      // to accumulate ~1 MB of retained state per cycle.
      const state = get();
      const viewPatch: Partial<ReturnType<Get_>> = {};
      if (state.showFrustum) viewPatch.showFrustum = false;
      if (state.workspaceMode === "inking" && state.inkingMode) {
        viewPatch.inkingMode = null;
      }
      const curve = isCurveObject(obj);
      // Restore persisted session state (granularity, hidden geometry)
      const cached = state.editSessionCache.get(objectId);
      clearLiveEditPreview();
      set({
        editMode: {
          objectId,
          selectedVertices: new Set(),
          selectedEdges: new Set(),
          selectedFaces: new Set(),
          hiddenVertices: cached?.hiddenVertices ?? new Set(),
          hiddenFaces: cached?.hiddenFaces ?? new Set(),
          granularity: cached?.granularity ?? "vertex",
          xray: cached?.xray ?? false,
          showVertexGroupColors: cached?.showVertexGroupColors ?? true,
          lastActiveVertex: null,
          lastActiveEdge: null,
          lastActiveFace: null,
          ...(curve && obj.curveData
            ? {
                isCurve: true,
                curveEditVertices: buildCurveEditVertices(obj.curveData),
              }
            : {}),
        },
        activeEditSession: null,
        mode: "edit" as InteractionMode,
        selectedObjectTargets: [{ kind: "sceneObject", id: objectId }],
        workspaceMode: "edit",
        canUndo: undoManager.canUndo,
        canRedo: undoManager.canRedo,
        // Cursor selection is mutually exclusive with edit selection.
        ...(state.cursor3D.selected
          ? { cursor3D: { ...state.cursor3D, selected: false } }
          : {}),
        ...viewPatch,
      });
    },

    exitEditMode: () => {
      // Cancel any in-flight command before leaving — prevents stale
      // commandBuffer/snapshot state from lingering into the next session.
      if (get().commandBuffer) {
        get().cancelCommand();
      }
      // No undo snapshot for mode transitions: applySnapshot re-enters edit
      // mode automatically when the next snapshot below on the stack carries
      // an editSelection. See enterEditMode for the paired rationale.
      // Stash session state so it survives exit/re-enter cycles
      const em = get().editMode;
      clearLiveEditPreview();
      if (em) {
        const cache = new Map(get().editSessionCache);
        cache.set(em.objectId, {
          granularity: em.granularity,
          hiddenVertices: new Set(em.hiddenVertices),
          hiddenFaces: new Set(em.hiddenFaces),
          xray: em.xray,
          showVertexGroupColors: em.showVertexGroupColors,
        });
        set({
          editMode: null,
          activeEditSession: null,
          editSessionCache: cache,
          mode: "navigate" as InteractionMode,
          activeOperator: null,
          workspaceMode: "object",
        });
      } else {
        set({
          editMode: null,
          activeEditSession: null,
          mode: "navigate" as InteractionMode,
          activeOperator: null,
          workspaceMode: "object",
        });
      }
      set({ canUndo: undoManager.canUndo, canRedo: undoManager.canRedo });
    },

    setEditGranularity: (granularity: "vertex" | "edge" | "face") => {
      const em = get().editMode;
      if (!em) return;
      const obj = get().sceneObjects.get(em.objectId);
      if (!obj?.topology) {
        set({
          editMode: {
            ...em,
            granularity,
            selectedEdges: new Set(),
            selectedFaces: new Set(),
          },
        });
        return;
      }
      const converted = convertSelectionForGranularity(
        obj.topology,
        em,
        granularity,
      );
      set({ editMode: { ...em, granularity, ...converted } });
    },

    toggleEditXray: () => {
      const em = get().editMode;
      if (!em) return;
      set({ editMode: { ...em, xray: !em.xray } });
    },

    setShowVertexGroupColors: (value: boolean) => {
      const em = get().editMode;
      if (!em) return;
      set({ editMode: { ...em, showVertexGroupColors: value } });
    },

    selectVertex: (index: number, additive = false) => {
      const em = get().editMode;
      if (!em) return;
      set({
        editMode: {
          ...em,
          selectedVertices: toggleSelection(
            em.selectedVertices,
            index,
            additive,
          ),
          lastActiveVertex: index,
        },
      });
    },

    selectVertices: (indices: number[], additive = false) => {
      const em = get().editMode;
      if (!em) return;
      set({
        editMode: {
          ...em,
          selectedVertices: toggleSelectionBatch(
            em.selectedVertices,
            indices,
            additive,
          ),
          lastActiveVertex: indices[0] ?? em.lastActiveVertex,
        },
      });
    },

    clearVertexSelection: () => {
      const em = get().editMode;
      if (!em) return;
      // Save current selection for recall before wiping it.
      if (hasAnySelection(em)) {
        set({ lastEditModeSelection: captureEditSelectionForRecall(em) });
      }
      const before = captureSelectionDeltaState(get());
      set({
        editMode: {
          ...em,
          selectedVertices: new Set(),
          selectedEdges: new Set(),
          selectedFaces: new Set(),
          lastActiveVertex: null,
          lastActiveEdge: null,
          lastActiveFace: null,
        },
      });
      const after = captureSelectionDeltaState(get());
      pushSelectionUndo(before, after);
      set({ canUndo: undoManager.canUndo, canRedo: undoManager.canRedo });
    },

    boxSelectEdit: (
      vertices: Set<number>,
      edges: Set<number>,
      faces: Set<number>,
      additive: boolean,
    ) => {
      const em = get().editMode;
      if (!em) return;
      const obj = get().sceneObjects.get(em.objectId);
      if (!obj) return;
      const before = captureSelectionDeltaState(get());

      const next = applyBoxSelection(
        obj.topology,
        em,
        { vertices, edges, faces },
        additive,
      );
      set({
        editMode: {
          ...em,
          ...next,
          lastActiveVertex: null,
          lastActiveEdge: null,
          lastActiveFace: null,
        },
      });
      const after = captureSelectionDeltaState(get());
      pushSelectionUndo(before, after);
      set({ canUndo: undoManager.canUndo, canRedo: undoManager.canRedo });
    },

    brushSelectEdit: (
      vertices: Set<number>,
      edges: Set<number>,
      faces: Set<number>,
      mode: BrushSelectionMode,
    ) => {
      const em = get().editMode;
      if (!em) return;
      const obj = get().sceneObjects.get(em.objectId);
      if (!obj) return;

      if (em.isCurve) {
        const selectedVertices =
          mode === "set" ? new Set<number>() : new Set(em.selectedVertices);
        if (mode === "subtract") {
          for (const vertex of vertices) selectedVertices.delete(vertex);
        } else {
          for (const vertex of vertices) selectedVertices.add(vertex);
        }
        set({
          editMode: {
            ...em,
            selectedVertices,
            selectedEdges: new Set(),
            selectedFaces: new Set(),
            lastActiveVertex: null,
            lastActiveEdge: null,
            lastActiveFace: null,
          },
        });
        return;
      }

      const next = applyBrushSelection(
        obj.topology,
        em,
        { vertices, edges, faces },
        mode,
      );
      set({
        editMode: {
          ...em,
          ...next,
          lastActiveVertex: null,
          lastActiveEdge: null,
          lastActiveFace: null,
        },
      });
    },

    selectConnectedFromSelection: () => {
      const em = get().editMode;
      if (!em) return;
      const obj = get().sceneObjects.get(em.objectId);
      if (!obj) return;

      const next = selectConnectedEditSelection(
        obj.topology,
        obj.vertices.length / 3,
        em,
      );
      if (!next) return;
      set({ editMode: { ...em, ...next } });
    },

    selectSimilarFacesByNormal: (thresholdDegrees: number = 0) => {
      const em = get().editMode;
      if (!em || !isFaceMode(em) || !hasFaceSelection(em)) return;
      const obj = get().sceneObjects.get(em.objectId);
      if (!obj?.topology) return;

      const capture = captureSelectionSeed(em);
      if (!capture) return;

      const next = selectSimilarFacesByNormal(
        obj.vertices,
        obj.topology,
        capture.seedFaces,
        capture.hiddenFacesAtSeedTime,
        thresholdDegrees,
      );
      const selectedVertices = deriveVerticesFromFaces(obj.topology, next);

      const lastOperation: LastOperation = {
        type: "selectSimilarNormal",
        params: { threshold: thresholdDegrees },
        replayKind: "selection-seed",
        capture,
      };

      set({
        editMode: {
          ...em,
          selectedFaces: next,
          selectedVertices,
          selectedEdges: new Set<number>(),
        },
        lastOperation,
      });
    },

    relaxSelectionToSurface: () => {
      const state = get();
      const em = state.editMode;
      if (!em || em.isCurve) return;
      const obj = state.sceneObjects.get(em.objectId);
      if (!obj?.topology || em.selectedVertices.size === 0) return;

      const surfaceContext = createSceneSurfaceProjectContext(
        new Set([em.objectId]),
        state.sceneObjects,
        state.collections,
        state.visibility,
      );
      if (surfaceContext.surfaces.length === 0) {
        disposeSceneSurfaceProjectContext(surfaceContext);
        return;
      }

      try {
        const adjVertexCount = obj.vertices.length / 3;
        const worldMatrix =
          composeSceneObjectWorldMatrix(
            state.sceneObjects,
            state.collections,
            em.objectId,
            _surfaceWorldMatrix,
          ) ?? _surfaceWorldMatrix.identity();
        const inverseWorld = _surfaceInverseMatrix.copy(worldMatrix).invert();

        const iterations = 6;
        const strength = 0.5;
        let currentWorld = new Float32Array(obj.vertices.length);
        let nextWorld = new Float32Array(obj.vertices.length);
        for (let i = 0; i < obj.vertices.length; i += 3) {
          _surfaceWorldPoint
            .set(obj.vertices[i], obj.vertices[i + 1], obj.vertices[i + 2])
            .applyMatrix4(worldMatrix);
          currentWorld[i] = _surfaceWorldPoint.x;
          currentWorld[i + 1] = _surfaceWorldPoint.y;
          currentWorld[i + 2] = _surfaceWorldPoint.z;
        }

        const topology = obj.topology;
        const adjacency = getAdjacencyCache(topology, adjVertexCount);

        for (let iteration = 0; iteration < iterations; iteration++) {
          nextWorld.set(currentWorld);
          for (const vi of em.selectedVertices) {
            if (em.hiddenVertices.has(vi)) continue;
            const neighbors = vertexNeighbors(adjacency, topology, vi);
            if (neighbors.length === 0) continue;

            let avgX = 0;
            let avgY = 0;
            let avgZ = 0;
            for (const neighbor of neighbors) {
              const base = neighbor * 3;
              avgX += currentWorld[base];
              avgY += currentWorld[base + 1];
              avgZ += currentWorld[base + 2];
            }
            const inv = 1 / neighbors.length;
            const base = vi * 3;
            const blendedX =
              currentWorld[base] + (avgX * inv - currentWorld[base]) * strength;
            const blendedY =
              currentWorld[base + 1] +
              (avgY * inv - currentWorld[base + 1]) * strength;
            const blendedZ =
              currentWorld[base + 2] +
              (avgZ * inv - currentWorld[base + 2]) * strength;

            const hit = queryNearestSceneSurface(surfaceContext, [
              blendedX,
              blendedY,
              blendedZ,
            ]);
            nextWorld[base] = hit?.worldPosition[0] ?? blendedX;
            nextWorld[base + 1] = hit?.worldPosition[1] ?? blendedY;
            nextWorld[base + 2] = hit?.worldPosition[2] ?? blendedZ;
          }
          [currentWorld, nextWorld] = [nextWorld, currentWorld];
        }

        const nextLocal = new Float32Array(obj.vertices);
        for (const vi of em.selectedVertices) {
          const base = vi * 3;
          _surfaceLocalPoint
            .set(
              currentWorld[base],
              currentWorld[base + 1],
              currentWorld[base + 2],
            )
            .applyMatrix4(inverseWorld);
          nextLocal[base] = _surfaceLocalPoint.x;
          nextLocal[base + 1] = _surfaceLocalPoint.y;
          nextLocal[base + 2] = _surfaceLocalPoint.z;
        }

        get().pushUndo();
        set({
          ...patchSceneObjectAfterEdit(state, em.objectId, {
            vertices: nextLocal,
            ...computeNormalsForObject(obj, {
              vertices: nextLocal,
              topology: obj.topology,
            }),
          }),
        });
      } finally {
        disposeSceneSurfaceProjectContext(surfaceContext);
      }
    },

    hideEditSelection: () => {
      const em = get().editMode;
      if (!em) return;
      const obj = get().sceneObjects.get(em.objectId);
      if (!obj) return;
      if (!hasAnySelection(em)) return;
      get().pushUndo();

      const hiddenVerts = new Set(em.hiddenVertices);
      const hiddenFaces = new Set(em.hiddenFaces);

      if (em.granularity === "face") {
        // Face mode: hide selected faces; vertices follow faces.
        for (const fi of em.selectedFaces) hiddenFaces.add(fi);
      } else {
        // Vertex/edge mode: hide selected vertices; any face touching a
        // hidden vertex also hides (matches Blender — no floating orphans).
        for (const vi of em.selectedVertices) hiddenVerts.add(vi);
        propagateHiddenVerticesToFaces(obj.topology, hiddenVerts, hiddenFaces);
      }
      syncHiddenVerticesToFaces(obj.topology, hiddenFaces, hiddenVerts);

      // Clear selection — hidden geometry cannot remain selected
      set({
        editMode: {
          ...em,
          hiddenVertices: hiddenVerts,
          hiddenFaces: hiddenFaces,
          selectedVertices: new Set(),
          selectedEdges: new Set(),
          selectedFaces: new Set(),
          lastActiveVertex: null,
          lastActiveEdge: null,
          lastActiveFace: null,
        },
      });
    },

    hideEditUnselected: () => {
      const em = get().editMode;
      if (!em) return;
      const obj = get().sceneObjects.get(em.objectId);
      if (!obj) return;
      get().pushUndo();

      const hiddenVerts = new Set(em.hiddenVertices);
      const hiddenFaces = new Set(em.hiddenFaces);
      const faceCount = topologyFaceCount(obj.topology);

      if (em.granularity === "face") {
        // Face mode: hide every face not in the selection.
        for (let fi = 0; fi < faceCount; fi++) {
          if (!em.selectedFaces.has(fi)) hiddenFaces.add(fi);
        }
      } else {
        // Vertex/edge mode: hide every unselected vertex, then propagate.
        const vertCount = obj.vertices.length / 3;
        for (let vi = 0; vi < vertCount; vi++) {
          if (!em.selectedVertices.has(vi)) hiddenVerts.add(vi);
        }
        propagateHiddenVerticesToFaces(obj.topology, hiddenVerts, hiddenFaces);
      }
      syncHiddenVerticesToFaces(obj.topology, hiddenFaces, hiddenVerts);

      // Selection remains on the now-isolated geometry
      set({
        editMode: {
          ...em,
          hiddenVertices: hiddenVerts,
          hiddenFaces: hiddenFaces,
        },
      });
    },

    unhideEditAll: () => {
      const em = get().editMode;
      if (!em) return;
      if (em.hiddenVertices.size === 0 && em.hiddenFaces.size === 0) return;
      get().pushUndo();
      set({
        editMode: {
          ...em,
          hiddenVertices: new Set(),
          hiddenFaces: new Set(),
        },
      });
    },

    moveSelectedVertices: (delta: [number, number, number]) => {
      const em = get().editMode;
      if (!em || !hasVertexSelection(em)) return;
      const obj = get().sceneObjects.get(em.objectId);
      if (!obj) return;
      const clipPlanes = collectClipPlanes(
        [],
        get().modifierOverrides.get(em.objectId) ?? [],
      );
      const srcVerts = obj.vertices;
      const verts = new Float32Array(srcVerts);
      for (const vi of em.selectedVertices) {
        verts[vi * 3] += delta[0];
        verts[vi * 3 + 1] += delta[1];
        verts[vi * 3 + 2] += delta[2];
      }
      if (clipPlanes.length > 0) {
        applyMirrorClipping(verts, clipPlanes, em.selectedVertices, srcVerts);
      }
      set({
        sceneObjects: patchSceneObject(get().sceneObjects, em.objectId, {
          vertices: verts,
        }),
      });
    },

    setVertexPositions: (objectId: string, vertices: Float32Array) => {
      // Skip normal recomputation during live drag for performance.
      // Normals are recomputed on confirm.
      set({
        sceneObjects: patchSceneObject(get().sceneObjects, objectId, {
          vertices,
        }),
      });
    },

    setSkinRadii: (objectId: string, skinRadii: Float32Array) => {
      set({
        sceneObjects: patchSceneObject(get().sceneObjects, objectId, {
          skinRadii,
        }),
      });
    },
  };
}
